/**
 * REST client for SMS phone verification (`/sms/verify/start` and
 * `/sms/verify/confirm` on the async HTTP API). Backs the "Text my agent"
 * setup wizard in Account settings.
 *
 * Auth is the Cognito **access token** as a bearer — the async API's JWT
 * authorizer reads the caller's `sub` from it (the phone is verified against
 * that signed-in identity, never trusted from the body). Mirrors
 * `sessionApi.ts`.
 */

import { getAccessToken } from '../auth/accessToken.ts';
import { asyncApiConfig } from '../config/async-api-config.ts';
import { requestTimeout } from '../config/requestTimeout.ts';

async function authHeaders(): Promise<Record<string, string>> {
  const jwt = await getAccessToken();
  return { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' };
}

async function errorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body.error === 'string' && body.error.length > 0) {
      return body.error;
    }
  } catch {
    // non-JSON body; fall through
  }
  return fallback;
}

/** Begin verification: texts a 6-digit code to `phone` from the agent's number. */
export async function startSmsVerification(phone: string): Promise<void> {
  const response = await fetch(`${asyncApiConfig.baseUrl}/sms/verify/start`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ phone }),
    signal: requestTimeout(),
  });
  if (!response.ok) {
    const msg = await errorMessage(
      response,
      response.status === 429
        ? 'A code was just sent — please wait a moment before retrying.'
        : 'Could not send a verification code. Check the number and try again.',
    );
    console.error(`[smsApi] start failed (${response.status})`);
    throw new Error(msg);
  }
}

/** Finish verification: confirms the code, links the phone, and triggers a welcome text. */
export async function confirmSmsVerification(phone: string, code: string): Promise<void> {
  const response = await fetch(`${asyncApiConfig.baseUrl}/sms/verify/confirm`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ phone, code }),
    signal: requestTimeout(),
  });
  if (!response.ok) {
    const msg = await errorMessage(response, 'That code didn’t match. Request a new one and try again.');
    console.error(`[smsApi] confirm failed (${response.status})`);
    throw new Error(msg);
  }
}

/**
 * Unlink the verified phone: clears the stored number and opts out. Identity is
 * the access-token `sub` (no body). `phone_number` / `custom:smsVerified` are
 * server-set only, so this teardown can't be done from the browser directly.
 */
export async function removeSmsVerification(): Promise<void> {
  const response = await fetch(`${asyncApiConfig.baseUrl}/sms/verify/remove`, {
    method: 'POST',
    headers: await authHeaders(),
    signal: requestTimeout(),
  });
  if (!response.ok) {
    const msg = await errorMessage(response, 'Could not remove your number. Please try again.');
    console.error(`[smsApi] remove failed (${response.status})`);
    throw new Error(msg);
  }
}
